/**
 * A very small PDF writer: line art and text in one colour.
 *
 * Written rather than imported because of what these maps need. The obvious
 * alternative — jsPDF plus svg2pdf.js reading the rendered SVG out of the DOM —
 * would have to survive exactly the features a plat sheet leans on: white halos
 * behind street names via `paint-order`, dash arrays in user units, rotated
 * text. Those are the features SVG-to-PDF converters get wrong. It would also
 * mean mounting all twenty-five sheets in the browser to convert them, which is
 * the thing generating a document was meant to stop doing.
 *
 * What is actually needed is small: paths, text in two standard fonts, and a
 * page box. That is this file.
 *
 * Coordinates are PDF user space — points, origin bottom-left, y upward. The
 * conversion from the map's grid lives in plat/sheet.ts; doing it there rather
 * than with a flipping matrix here keeps text from coming out mirrored.
 *
 * This file knows nothing about what is drawn on a page. The page itself — the
 * border, the running bars, the marks a form is made of — is pdf/page.ts.
 */

export const PT_PER_IN = 72;
/** CSS px are 1/96in; the plat model measures its type in them. */
export const PT_PER_PX = 72 / 96;

export type Pt = { x: number; y: number };

/**
 * Advance widths for the two standard fonts, per 1000 units, for ASCII.
 *
 * Carried so text can actually be centred. The model's fit tests use a flat
 * 0.62-per-character estimate, which is the right tool for deciding whether a
 * house number belongs in a lot at all; it is the wrong tool for deciding where
 * the number starts.
 */
const W_REGULAR = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
  556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];
const W_BOLD = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
  975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
  333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
  611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
];

/**
 * Characters outside ASCII that turn up in ward data, mapped to WinAnsi.
 *
 * The em dash separates an address from a surname on every indexed-lot line,
 * so this is not a hypothetical: without it the dash would print as a question
 * mark on hundreds of entries.
 */
const WIN_ANSI: Record<string, [number, number]> = {
  '—': [0x97, 1000],  // em dash
  '–': [0x96, 556],   // en dash
  '‘': [0x91, 222],
  '’': [0x92, 222],
  '“': [0x93, 333],
  '”': [0x94, 333],
  '…': [0x85, 1000],  // ellipsis
  '·': [0xb7, 278],   // middle dot, as in "1 · West End"
};

function charByte(ch: string): number {
  const mapped = WIN_ANSI[ch];
  if (mapped) return mapped[0];
  const c = ch.charCodeAt(0);
  // Latin-1 passes through: WinAnsi agrees with it from 0xA0 up, which covers
  // the accented letters in the directory.
  if (c >= 32 && c <= 126) return c;
  if (c >= 0xa0 && c <= 0xff) return c;
  return 0x3f;   // '?'
}

function charWidth(ch: string, bold: boolean): number {
  const mapped = WIN_ANSI[ch];
  if (mapped) return mapped[1];
  const c = ch.charCodeAt(0);
  const table = bold ? W_BOLD : W_REGULAR;
  if (c >= 32 && c <= 126) return table[c - 32];
  return bold ? 611 : 556;   // accented letters, near enough
}

/** Width of a string at a given size, in points. */
export function textWidth(s: string, size: number, bold = false): number {
  let w = 0;
  for (const ch of s) w += charWidth(ch, bold);
  return (w / 1000) * size;
}

/** A PDF literal string: single-byte characters with the three escapes. */
function pdfString(s: string): string {
  let out = '';
  for (const ch of s) {
    const b = charByte(ch);
    const c = String.fromCharCode(b);
    out += (c === '(' || c === ')' || c === '\\') ? `\\${c}` : c;
  }
  return out;
}

/**
 * A string for the document information dictionary.
 *
 * Not the same encoding as page text: literal strings there are read as
 * PDFDocEncoding, in which the em dash of "Lorraine Ward — sector maps" is an
 * S-caron. UTF-16BE with a byte-order mark leaves nothing to interpret.
 */
function pdfTextString(s: string): string {
  let hex = 'FEFF';
  for (const ch of s) {
    hex += ch.charCodeAt(0).toString(16).padStart(4, '0').toUpperCase();
  }
  return `<${hex}>`;
}

const n = (v: number) => {
  const r = Math.round(v * 100) / 100;
  return Object.is(r, -0) ? '0' : String(r);
};

export interface TextOptions {
  size: number;
  bold?: boolean;
  /** Where (x, y) sits along the string. */
  align?: 'left' | 'center' | 'right';
  /** Degrees counter-clockwise, PDF sense. */
  rotate?: number;
  /** 0 is black, 1 is white. */
  gray?: number;
  /**
   * Width of a white outline drawn behind the glyphs, in points. Stands in for
   * SVG's paint-order:stroke — a street name has to stay readable where it
   * crosses a lot line. Done as two passes, stroke then fill, because a single
   * stroked-and-filled pass puts the halo on top of the letters.
   */
  halo?: number;
  /**
   * Extra space between characters, in points. Small type set in capitals —
   * a section heading, a label — closes up and reads as a block; a little
   * air puts the letters back. Counted into the string's width so a centred
   * or right-aligned tracked line still lands where it was asked to.
   */
  tracking?: number;
}

export class PdfPage {
  readonly ops: string[] = [];

  constructor(readonly w: number, readonly h: number) {}

  save() { this.ops.push('q'); }
  restore() { this.ops.push('Q'); }
  /** Stroke grey. Opacity is emulated by lightening: everything is on white. */
  strokeGray(g: number) { this.ops.push(`${n(g)} G`); }
  fillGray(g: number) { this.ops.push(`${n(g)} g`); }
  lineWidth(w: number) { this.ops.push(`${n(w)} w`); }
  lineJoinRound() { this.ops.push('1 j'); }
  dash(on: number, off: number) {
    this.ops.push(on > 0 ? `[${n(on)} ${n(off)}] 0 d` : '[] 0 d');
  }

  /** Clip everything after this to a rectangle, until the next restore(). */
  clipRect(x: number, y: number, w: number, h: number) {
    this.ops.push(`${n(x)} ${n(y)} ${n(w)} ${n(h)} re W n`);
  }

  /**
   * Clip to a set of rings, until the next restore().
   *
   * Non-zero winding, matching how the same rings are filled elsewhere. The
   * `n` ends the path without painting it: `W` only marks it as the clip.
   */
  clipPoly(rings: Pt[][]) {
    let d = '';
    for (const ring of rings) {
      if (ring.length < 3) continue;
      d += `${n(ring[0].x)} ${n(ring[0].y)} m`;
      for (let i = 1; i < ring.length; i++) d += ` ${n(ring[i].x)} ${n(ring[i].y)} l`;
      d += ' h ';
    }
    if (d) this.ops.push(`${d}W n`);
  }

  rect(x: number, y: number, w: number, h: number, mode: 'S' | 'f' | 'B' = 'S') {
    this.ops.push(`${n(x)} ${n(y)} ${n(w)} ${n(h)} re ${mode}`);
  }

  poly(pts: Pt[], close: boolean, mode: 'S' | 'f' | 'B' = 'S') {
    if (pts.length < 2) return;
    let d = `${n(pts[0].x)} ${n(pts[0].y)} m`;
    for (let i = 1; i < pts.length; i++) d += ` ${n(pts[i].x)} ${n(pts[i].y)} l`;
    this.ops.push(`${d}${close ? ' h' : ''} ${mode}`);
  }

  /** Many disjoint segments as one path — how outlines arrive from the model. */
  segments(list: [Pt, Pt][]) {
    if (!list.length) return;
    let d = '';
    for (const [a, b] of list) {
      d += `${n(a.x)} ${n(a.y)} m ${n(b.x)} ${n(b.y)} l `;
    }
    this.ops.push(`${d}S`);
  }

  circle(cx: number, cy: number, r: number, mode: 'S' | 'f' = 'f') {
    const k = r * 0.5523;
    this.ops.push(
      `${n(cx + r)} ${n(cy)} m `
      + `${n(cx + r)} ${n(cy + k)} ${n(cx + k)} ${n(cy + r)} ${n(cx)} ${n(cy + r)} c `
      + `${n(cx - k)} ${n(cy + r)} ${n(cx - r)} ${n(cy + k)} ${n(cx - r)} ${n(cy)} c `
      + `${n(cx - r)} ${n(cy - k)} ${n(cx - k)} ${n(cy - r)} ${n(cx)} ${n(cy - r)} c `
      + `${n(cx + k)} ${n(cy - r)} ${n(cx + r)} ${n(cy - k)} ${n(cx + r)} ${n(cy)} c ${mode}`,
    );
  }

  text(s: string, x: number, y: number, o: TextOptions) {
    if (!s) return;
    const bold = !!o.bold;
    const font = bold ? '/F2' : '/F1';
    const track = o.tracking ?? 0;
    const w = textWidth(s, o.size, bold) + track * Math.max(0, s.length - 1);
    const shift = o.align === 'center' ? -w / 2 : o.align === 'right' ? -w : 0;
    const rad = ((o.rotate ?? 0) * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    // The anchor shift runs along the text's own baseline, so it has to be
    // rotated with it — otherwise a turned street name slides off its road.
    const tx = x + shift * cos;
    const ty = y + shift * sin;
    const body = `${font} ${n(o.size)} Tf ${n(track)} Tc `
      + `${n(cos)} ${n(sin)} ${n(-sin)} ${n(cos)} ${n(tx)} ${n(ty)} Tm `
      + `(${pdfString(s)}) Tj`;
    if (o.halo) {
      this.ops.push(`q 1 G ${n(o.halo)} w 1 j BT 1 Tr ${body} ET Q`);
    }
    this.ops.push(`BT ${n(o.gray ?? 0)} g 0 Tr ${body} ET`);
  }
}

async function deflate(bytes: Uint8Array): Promise<Uint8Array | null> {
  // Safari 16.4+ and Chrome 80+. Absent it the file is simply larger, so this
  // is an optimisation and never a correctness question.
  const CS = (globalThis as { CompressionStream?: typeof CompressionStream })
    .CompressionStream;
  if (!CS) return null;
  try {
    const stream = new Blob([bytes as BlobPart]).stream()
      .pipeThrough(new CS('deflate'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

const latin1 = (s: string): Uint8Array => {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
};

export class PdfDoc {
  private pages: PdfPage[] = [];

  constructor(private title: string) {}

  addPage(wPt: number, hPt: number): PdfPage {
    const p = new PdfPage(wPt, hPt);
    this.pages.push(p);
    return p;
  }

  get pageCount() { return this.pages.length; }

  /** Every page so far, for furniture that can only be drawn once the
   *  document is complete — a footer saying "3 of 18", for instance. */
  get all(): PdfPage[] { return this.pages; }

  async blob(): Promise<Blob> {
    const chunks: Uint8Array[] = [];
    const offsets: number[] = [];
    let len = 0;
    const push = (b: Uint8Array) => { chunks.push(b); len += b.length; };
    const pushText = (s: string) => push(latin1(s));

    // 1 catalog, 2 pages, 3 Helvetica, 4 Helvetica-Bold, 5 info, then two
    // objects per page.
    const FIRST_PAGE_OBJ = 6;
    const objCount = 5 + this.pages.length * 2;
    const openObj = (i: number) => {
      offsets[i] = len;
      pushText(`${i} 0 obj\n`);
    };
    const closeObj = () => pushText('\nendobj\n');

    pushText('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n');

    const kids = this.pages
      .map((_, i) => `${FIRST_PAGE_OBJ + i * 2} 0 R`).join(' ');

    openObj(1);
    pushText('<< /Type /Catalog /Pages 2 0 R >>');
    closeObj();

    openObj(2);
    pushText(`<< /Type /Pages /Kids [${kids}] /Count ${this.pages.length} >>`);
    closeObj();

    openObj(3);
    pushText('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica '
      + '/Encoding /WinAnsiEncoding >>');
    closeObj();

    openObj(4);
    pushText('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold '
      + '/Encoding /WinAnsiEncoding >>');
    closeObj();

    const d = new Date();
    const two = (v: number) => String(v).padStart(2, '0');
    const stamp = `D:${d.getFullYear()}${two(d.getMonth() + 1)}${two(d.getDate())}`
      + `${two(d.getHours())}${two(d.getMinutes())}${two(d.getSeconds())}`;
    openObj(5);
    pushText(`<< /Title ${pdfTextString(this.title)} /Producer (Steward) `
      + `/CreationDate (${stamp}) >>`);
    closeObj();

    for (let i = 0; i < this.pages.length; i++) {
      const page = this.pages[i];
      const pageObj = FIRST_PAGE_OBJ + i * 2;
      const contentObj = pageObj + 1;

      openObj(pageObj);
      pushText(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${n(page.w)} ${n(page.h)}] `
        + '/Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> '
        + `/Contents ${contentObj} 0 R >>`);
      closeObj();

      const raw = latin1(page.ops.join('\n'));
      const packed = await deflate(raw);
      openObj(contentObj);
      pushText(`<< /Length ${(packed ?? raw).length}`
        + `${packed ? ' /Filter /FlateDecode' : ''} >>\nstream\n`);
      push(packed ?? raw);
      pushText('\nendstream');
      closeObj();
    }

    const xref = len;
    let table = `xref\n0 ${objCount + 1}\n0000000000 65535 f \n`;
    for (let i = 1; i <= objCount; i++) {
      table += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
    }
    pushText(table);
    pushText(`trailer\n<< /Size ${objCount + 1} /Root 1 0 R /Info 5 0 R >>\n`
      + `startxref\n${xref}\n%%EOF\n`);

    return new Blob(chunks as BlobPart[], { type: 'application/pdf' });
  }
}
